import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Search, Plus, Edit3, Trash2, Eye, X, Shield, Crown, Star, Coffee,
  Mail, Phone, MapPin, Briefcase, Calendar, Building, CreditCard, FileText,
  UserCheck, UserX, Lock, CheckCircle, AlertTriangle, Filter, Grid3x3, List,
  ChevronRight, ScrollText, PoundSterling, Cake, Fingerprint, Upload, Download,
  ShieldCheck, ShieldAlert, Signature, Clock, Info, Globe, Home, MessageCircle, Layers,
} from 'lucide-react';
import { FilterChips } from '../../components/people/FilterChips';
import { DirectoryAlphaBar } from '../../components/people/DirectoryAlphaBar';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField } from '../../components/ui/jewel-modal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ══════════════════════════════════════════════════════════════════
   PRIMITIVES
   ══════════════════════════════════════════════════════════════════ */
const ROLE_META = {
  superadmin: { hex: '#F59E0B', label: 'Super Admin', icon: Crown },
  executive:  { hex: '#8A2BE2', label: 'Executive',   icon: Shield },
  admin:      { hex: '#00E5FF', label: 'Admin',       icon: Shield },
  manager:    { hex: '#10B981', label: 'Manager',     icon: Star },
  staff:      { hex: '#94A3B8', label: 'Staff',       icon: Coffee },
};

const STATUS_META = {
  active:    { hex: '#10B981', label: 'Active',    icon: CheckCircle },
  suspended: { hex: '#F59E0B', label: 'Suspended', icon: AlertTriangle },
  locked:    { hex: '#F43F5E', label: 'Locked',    icon: Lock },
};

const BentoCard = ({ children, className = '', accent = '#00E5FF', ...rest }) => (
  <div
    className={`relative overflow-hidden rounded-3xl border border-white/[0.06] backdrop-blur-md ${className}`}
    style={{ background: 'linear-gradient(140deg, rgba(10,10,20,0.65) 0%, rgba(8,8,18,0.85) 100%)', boxShadow: '0 20px 50px -20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.03)' }}
    {...rest}>
    <div className="pointer-events-none absolute -top-24 -right-16 w-56 h-56 rounded-full blur-[100px] opacity-40" style={{ background: `radial-gradient(circle, ${accent}30 0%, transparent 70%)` }} />
    <div className="relative">{children}</div>
  </div>
);

const StatCard = ({ icon: Icon, label, value, accent, sub }) => (
  <BentoCard accent={accent} className="!rounded-2xl">
    <div className="px-4 py-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}18`, border: `1px solid ${accent}35` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} strokeWidth={1.8} />
        </div>
        <p className="text-[9px] tracking-[0.22em] uppercase text-white/40 font-medium">{label}</p>
      </div>
      <p className="text-white text-2xl font-light tabular-nums leading-none">{value}</p>
      {sub && <p className="text-white/40 text-[10px] mt-1.5">{sub}</p>}
    </div>
  </BentoCard>
);

/* ══════════════════════════════════════════════════════════════════
   PROFILE CARD (grid tile)
   ══════════════════════════════════════════════════════════════════ */
const ProfileCard = ({ entry, onOpen, onMessage, online, isSelf, isExecutive, index = 0 }) => {
  const role = ROLE_META[entry.role] || ROLE_META.staff;
  const status = STATUS_META[entry.account_status || 'active'] || STATUS_META.active;
  const initials = (entry.display_name || entry.username || '?').split(' ').map(w => w.charAt(0)).join('').slice(0, 2).toUpperCase();
  return (
    <div
      role="button" tabIndex={0}
      onClick={() => onOpen(entry)}
      onKeyDown={(e) => e.key === 'Enter' && onOpen(entry)}
      data-testid={`staff-card-${entry.username}`}
      className="group relative cursor-pointer overflow-hidden rounded-3xl border border-white/[0.07] bg-[#0A0A12]/70 backdrop-blur-md p-5 text-left transition-[transform,border-color] duration-300 hover:border-white/[0.16] hover:-translate-y-0.5 animate-[jewelFadeUp_0.4s_ease-out_both]"
      style={{ boxShadow: '0 20px 40px -20px rgba(0,0,0,0.5)', animationDelay: `${Math.min(index, 12) * 35}ms` }}
    >
      {/* Role glow */}
      <div className="pointer-events-none absolute -top-16 -right-8 w-40 h-40 rounded-full blur-[80px] opacity-40 group-hover:opacity-80 transition-opacity" style={{ background: role.hex }} />
      <div className="relative">
        <div className="flex items-start gap-3 mb-4">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-medium"
              style={{
                background: `linear-gradient(135deg, ${role.hex}25, rgba(138,43,226,0.20))`,
                color: '#fff',
                border: `1px solid ${role.hex}40`,
                boxShadow: `0 0 20px -4px ${role.hex}55`,
              }}
            >
              {initials}
            </div>
            {online ? (
              <span className="absolute -bottom-1 -right-1 flex items-center justify-center" data-testid={`dir-online-${entry.username}`} title="Online now">
                <span className="absolute w-4 h-4 rounded-full bg-emerald-400/60 animate-ping" />
                <span className="relative w-4 h-4 rounded-full border-2 border-[#0A0A12] bg-emerald-400" />
              </span>
            ) : (
              <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-[#0A0A12]"
                style={{ background: status.hex, opacity: 0.55 }} title={status.label} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-[15px] font-medium tracking-tight truncate">{entry.display_name || entry.username}</p>
            <p className="text-white/45 text-[11px] truncate">@{entry.username}</p>
          </div>
          <span
            className="px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.12em] uppercase border flex-shrink-0"
            style={{ background: `${role.hex}18`, borderColor: `${role.hex}40`, color: role.hex }}
          >
            {role.label}
          </span>
        </div>

        <div className="space-y-1.5">
          {entry.title && (
            <div className="flex items-center gap-2 text-[11px] text-white/55">
              <Briefcase className="w-3 h-3 text-white/30" /> <span className="truncate">{entry.title}</span>
            </div>
          )}
          {entry.department && (
            <div className="flex items-center gap-2 text-[11px] text-white/55">
              <Building className="w-3 h-3 text-white/30" /> <span className="truncate">{entry.department}</span>
            </div>
          )}
          {entry.email && (
            <div className="flex items-center gap-2 text-[11px] text-white/55">
              <Mail className="w-3 h-3 text-white/30" /> <span className="truncate">{entry.email}</span>
            </div>
          )}
        </div>

        <div className="mt-4 pt-3 border-t border-white/[0.05] flex items-center justify-between">
          <span
            className="inline-flex items-center gap-1 text-[9px] font-semibold tracking-[0.14em] uppercase"
            style={{ color: status.hex }}
          >
            <status.icon className="w-2.5 h-2.5" /> {status.label}
          </span>
          <span className="flex items-center gap-1.5">
            {entry.contract_accepted ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.14em] uppercase border border-emerald-400/30 bg-emerald-500/10 text-emerald-300" title="Contract signed">
                <ShieldCheck className="w-2.5 h-2.5" /> Signed
              </span>
            ) : isExecutive ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.14em] uppercase border border-rose-400/25 bg-rose-500/10 text-rose-300" title="Contract not yet accepted">
                <ShieldAlert className="w-2.5 h-2.5" /> Unsigned
              </span>
            ) : null}
          </span>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2" onClick={(e) => e.stopPropagation()}>
          <button type="button" onClick={() => onOpen(entry)} data-testid={`dir-view-${entry.username}`}
            className="inline-flex items-center justify-center gap-1.5 py-2 rounded-xl text-[11px] font-medium text-white/70 border border-white/[0.08] bg-white/[0.02] hover:text-white hover:border-white/20 transition-colors">
            <Eye className="w-3.5 h-3.5" /> View profile
          </button>
          <button type="button" onClick={() => onMessage(entry)} disabled={isSelf} data-testid={`dir-message-${entry.username}`}
            className="inline-flex items-center justify-center gap-1.5 py-2 rounded-xl text-[11px] font-medium text-cyan-100 border border-cyan-400/30 bg-cyan-500/10 hover:bg-cyan-500/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <MessageCircle className="w-3.5 h-3.5" /> Message
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════
   DETAIL MODAL — with tabbed audit view
   ══════════════════════════════════════════════════════════════════ */
const DetailModal = ({ open, onOpenChange, entry, isExecutive, isSuperadmin, isManagement, onEdit, onDelete, onStatus, onUpload, uploading }) => {
  const [tab, setTab] = useState('overview');
  useEffect(() => { if (open) setTab('overview'); }, [open, entry?.id]);
  if (!entry) return null;

  const role = ROLE_META[entry.role] || ROLE_META.staff;
  const status = STATUS_META[entry.account_status || 'active'] || STATUS_META.active;

  const TABS = [
    { id: 'overview',   label: 'Overview',    icon: UserCheck,  needs: [] },
    { id: 'employment', label: 'Employment',  icon: Briefcase,  needs: [] },
    { id: 'personal',   label: 'Personal',    icon: Home,       needs: ['exec'] },
    { id: 'contract',   label: 'Contract Log', icon: Signature, needs: ['exec'] },
    { id: 'docs',       label: 'Documents',   icon: FileText,   needs: ['exec'] },
  ].filter(t => !t.needs.includes('exec') || isExecutive);

  const Row = ({ icon: Ic, label, value, mono }) => (
    <div className="flex items-start gap-2.5 py-2 border-b border-white/[0.04] last:border-b-0">
      {Ic && <Ic className="w-3.5 h-3.5 text-white/30 mt-0.5 flex-shrink-0" />}
      <span className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium w-32 flex-shrink-0 pt-0.5">{label}</span>
      <span className={`text-white/85 text-sm flex-1 min-w-0 break-words ${mono ? 'font-mono' : ''}`}>{value || <span className="text-white/25 italic">—</span>}</span>
    </div>
  );

  return (
    <JewelModal
      open={open} onOpenChange={onOpenChange}
      title={entry.display_name || entry.username}
      subtitle={`@${entry.username} · ${role.label}`}
      icon={role.icon} accent="cyan" size="2xl" testid="staff-detail-modal"
      footer={
        isManagement ? (
          <div className="flex items-center justify-between w-full flex-wrap gap-2">
            <div className="flex gap-2">
              {isExecutive && (
                <JewelButton variant="ghost" icon={Edit3} onClick={() => onEdit(entry)} data-testid="edit-staff-btn">Edit</JewelButton>
              )}
              <JewelButton variant="ghost" icon={Lock} onClick={() => onStatus(entry)} data-testid="status-staff-btn">Change status</JewelButton>
            </div>
            {isSuperadmin && (
              <JewelButton variant="ghost" icon={Trash2} onClick={() => onDelete(entry)} className="!text-rose-300 hover:!text-rose-200" data-testid="delete-staff-btn">Delete</JewelButton>
            )}
          </div>
        ) : null
      }
    >
      {/* Hero strip */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] p-4 mb-4"
        style={{ background: `linear-gradient(135deg, ${role.hex}12, rgba(138,43,226,0.06))` }}>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-lg font-medium flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${role.hex}30, rgba(138,43,226,0.25))`, border: `1px solid ${role.hex}50` }}>
            {(entry.display_name || entry.username).split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.14em] uppercase border" style={{ background: `${role.hex}18`, borderColor: `${role.hex}40`, color: role.hex }}>
                <role.icon className="w-2.5 h-2.5" /> {role.label}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.14em] uppercase" style={{ color: status.hex }}>
                <status.icon className="w-2.5 h-2.5" /> {status.label}
              </span>
              {entry.contract_accepted && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.14em] uppercase border border-emerald-400/30 bg-emerald-500/10 text-emerald-300">
                  <ShieldCheck className="w-2.5 h-2.5" /> Contract signed
                </span>
              )}
            </div>
            <p className="text-white/60 text-xs">{entry.title || 'No title'} · {entry.department || 'No department'}</p>
          </div>
        </div>
        {entry.suspension_reason && (
          <div className="mt-3 p-2.5 rounded-xl bg-rose-500/10 border border-rose-400/25 text-rose-200 text-xs">
            <span className="font-semibold">Suspension reason:</span> {entry.suspension_reason}
            {entry.suspended_by && <span className="text-rose-300/60"> · by {entry.suspended_by}</span>}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 overflow-x-auto jewel-scroll">
        {TABS.map(t => {
          const active = tab === t.id;
          return (
            <button
              key={t.id} type="button" onClick={() => setTab(t.id)}
              data-testid={`staff-detail-tab-${t.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-medium tracking-[0.14em] uppercase whitespace-nowrap transition-all border"
              style={{
                background: active ? 'linear-gradient(135deg, rgba(0,229,255,0.18), rgba(138,43,226,0.10))' : 'transparent',
                borderColor: active ? 'rgba(0,229,255,0.35)' : 'rgba(255,255,255,0.06)',
                color: active ? '#fff' : 'rgba(255,255,255,0.5)',
              }}
            >
              <t.icon className="w-3 h-3" /> {t.label}
              {t.id === 'contract' && entry.contract_acceptances_count > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full text-[8px] bg-emerald-500/20 text-emerald-300 tabular-nums">{entry.contract_acceptances_count}</span>
              )}
              {t.id === 'docs' && entry.documents?.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full text-[8px] bg-cyan-500/20 text-cyan-200 tabular-nums">{entry.documents.length}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Panels */}
      {tab === 'overview' && (
        <div className="space-y-1">
          <Row icon={UserCheck} label="Username" value={entry.username} mono />
          <Row icon={Mail}      label="Email"    value={entry.email} />
          <Row icon={Briefcase} label="Title"    value={entry.title} />
          <Row icon={Building}  label="Dept"     value={entry.department} />
          {isExecutive && <>
            <Row icon={Phone} label="Phone" value={entry.phone_number} />
            <Row icon={UserCheck} label="Legal name" value={entry.full_legal_name} />
          </>}
        </div>
      )}

      {tab === 'employment' && (
        <div className="space-y-1">
          <Row icon={Calendar}  label="Start date" value={entry.start_date} />
          <Row icon={FileText}  label="Contract"   value={entry.contract_type} />
          <Row icon={UserCheck} label="Manager"    value={entry.direct_manager} />
          {isExecutive && (
            <>
              <Row icon={PoundSterling} label="Hourly" value={entry.hourly_rate ? `£${entry.hourly_rate}` : null} />
              <Row icon={PoundSterling} label="Salary" value={entry.salary ? `£${Number(entry.salary).toLocaleString()}` : null} />
              <Row icon={CreditCard} label="Bank name" value={entry.bank_account_name} />
              <Row icon={CreditCard} label="Sort code" value={entry.bank_sort_code} mono />
              <Row icon={CreditCard} label="Account #" value={entry.bank_account_number} mono />
              <Row icon={Fingerprint} label="NI number" value={entry.national_insurance} mono />
            </>
          )}
        </div>
      )}

      {tab === 'personal' && isExecutive && (
        <div className="space-y-1">
          <Row icon={Cake} label="Date of birth" value={entry.date_of_birth} />
          <Row icon={Home} label="Address" value={entry.address} />
          <Row icon={MapPin} label="City" value={entry.city} />
          <Row icon={MapPin} label="Postcode" value={entry.postcode} mono />
          <Row icon={Globe} label="Country" value={entry.country} />
          <Row icon={UserCheck} label="Emergency" value={entry.emergency_contact_name} />
          <Row icon={Phone}     label="Emergency #" value={entry.emergency_contact_phone} />
          {entry.notes && (
            <div className="mt-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
              <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 mb-1.5">HR notes</p>
              <p className="text-white/80 text-sm whitespace-pre-wrap">{entry.notes}</p>
            </div>
          )}
        </div>
      )}

      {tab === 'contract' && isExecutive && (
        <div className="space-y-3">
          {(!entry.contract_acceptances || entry.contract_acceptances.length === 0) ? (
            <div className="text-center py-10">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-400/25 mb-3">
                <ShieldAlert className="w-6 h-6 text-rose-300" strokeWidth={1.6} />
              </div>
              <p className="text-white/60">No contract on file yet</p>
              <p className="text-white/35 text-xs mt-1">They'll be prompted to sign the next time they sign in.</p>
            </div>
          ) : entry.contract_acceptances.map((a, i) => (
            <div key={i} className="relative overflow-hidden rounded-2xl border border-emerald-400/20 bg-gradient-to-br from-emerald-500/[0.05] to-cyan-500/[0.03] p-4" data-testid={`contract-acceptance-row-${i}`}>
              <span className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-emerald-400 to-cyan-500" />
              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck className="w-4 h-4 text-emerald-300" />
                    <span className="text-white text-sm font-medium">Contract {a.contract_version}</span>
                    {i === (entry.contract_acceptances.length - 1) && (
                      <span className="px-1.5 py-0.5 rounded-full text-[8px] font-semibold tracking-[0.12em] uppercase bg-emerald-500/20 border border-emerald-400/40 text-emerald-200">Latest</span>
                    )}
                  </div>
                  <p className="text-white/50 text-[11px]">
                    <Clock className="inline w-3 h-3 mr-1 opacity-60" />
                    {a.accepted_at ? new Date(a.accepted_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] tracking-[0.2em] uppercase text-white/40">Signed by</p>
                  <p className="text-white text-sm font-medium">{a.full_legal_name || entry.display_name}</p>
                  <p className="italic text-cyan-200 text-xs" style={{ fontFamily: 'cursive' }}>{a.signature}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div className="p-2 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                  <p className="text-white/40 tracking-[0.15em] uppercase text-[9px] mb-0.5">Source IP</p>
                  <p className="text-white/80 font-mono">{a.accepted_ip || 'unknown'}</p>
                </div>
                <div className="p-2 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                  <p className="text-white/40 tracking-[0.15em] uppercase text-[9px] mb-0.5">User agent</p>
                  <p className="text-white/70 text-[10px] truncate" title={a.accepted_user_agent}>{a.accepted_user_agent || 'unknown'}</p>
                </div>
              </div>
              <div className="flex gap-1.5 mt-2.5 flex-wrap">
                {a.acknowledgements?.confidentiality && <span className="px-2 py-0.5 rounded-full text-[9px] tracking-wide uppercase bg-emerald-500/10 border border-emerald-400/25 text-emerald-300">Confidentiality ✓</span>}
                {a.acknowledgements?.conduct && <span className="px-2 py-0.5 rounded-full text-[9px] tracking-wide uppercase bg-emerald-500/10 border border-emerald-400/25 text-emerald-300">Conduct ✓</span>}
                {a.acknowledgements?.gdpr && <span className="px-2 py-0.5 rounded-full text-[9px] tracking-wide uppercase bg-emerald-500/10 border border-emerald-400/25 text-emerald-300">UK GDPR ✓</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'docs' && isExecutive && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium">HR documents</p>
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-medium tracking-[0.14em] uppercase text-cyan-200 bg-cyan-500/10 border border-cyan-400/30 cursor-pointer hover:bg-cyan-500/20 transition-colors" data-testid="upload-doc-btn">
              <Upload className="w-3 h-3" /> {uploading ? 'Uploading…' : 'Upload'}
              <input type="file" onChange={onUpload} className="hidden" disabled={uploading} />
            </label>
          </div>
          {(!entry.documents || entry.documents.length === 0) ? (
            <p className="text-center text-white/40 text-sm py-8">No documents uploaded</p>
          ) : entry.documents.map(d => (
            <div key={d.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
              <div className="w-9 h-9 rounded-lg bg-cyan-500/15 border border-cyan-400/25 flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-cyan-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-medium truncate">{d.filename}</p>
                <p className="text-white/40 text-[10px]">{d.document_type} · {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString('en-GB') : '—'}</p>
              </div>
              <a href={`${BACKEND_URL}${d.file_path}`} target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 text-[10px] tracking-[0.14em] uppercase font-medium">Open</a>
            </div>
          ))}
        </div>
      )}
    </JewelModal>
  );
};

/* ══════════════════════════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════════════════════════ */
const StaffDirectoryPage = ({ getAuthHeaders, currentUser, setActivePage }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [isExecutive, setIsExecutive] = useState(false);
  const [isManagement, setIsManagement] = useState(false);
  const [managersList, setManagersList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [contractFilter, setContractFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [letterFilter, setLetterFilter] = useState(null);
  const [onlineSet, setOnlineSet] = useState(new Set());

  // Modals
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusAction, setStatusAction] = useState({ status: '', reason: '' });
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const emptyForm = {
    username: '', display_name: '', title: '', department: '', email: '',
    full_legal_name: '', date_of_birth: '', phone_number: '',
    emergency_contact_name: '', emergency_contact_phone: '',
    address: '', city: '', postcode: '', country: '',
    start_date: '', contract_type: 'full-time',
    hourly_rate: '', salary: '',
    bank_account_name: '', bank_sort_code: '', bank_account_number: '',
    national_insurance: '', direct_manager: '', notes: '',
  };
  const [formData, setFormData] = useState(emptyForm);

  const fetchEntries = async () => {
    try {
      const h = getAuthHeaders(); if (!h) return;
      const r = await axios.get(`${BACKEND_URL}/api/staff-directory/`, { headers: h });
      setEntries(r.data.entries || []);
      setIsSuperadmin(!!r.data.is_superadmin);
      setIsExecutive(!!r.data.is_executive);
      setIsManagement(!!r.data.is_management);
    } catch (e) { toast.error('Could not load directory'); }
    finally { setLoading(false); }
  };
  const fetchManagers = async () => {
    try {
      const h = getAuthHeaders(); if (!h) return;
      const r = await axios.get(`${BACKEND_URL}/api/staff-directory/managers/list`, { headers: h });
      setManagersList(r.data || []);
    } catch {}
  };
  useEffect(() => { fetchEntries(); fetchManagers(); }, []);

  // Presence is admin-scoped; non-admins simply see no live dots
  useEffect(() => {
    const load = async () => {
      const h = getAuthHeaders(); if (!h) return;
      try {
        const r = await axios.get(`${BACKEND_URL}/api/admin/people/online-presence`, { headers: h });
        setOnlineSet(new Set((r.data.online || []).map(o => o.username)));
      } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const openDM = (entry) => {
    if (!setActivePage) return toast.error('Open Staff Chat to message');
    sessionStorage.setItem('staff_chat_open_dm', entry.username);
    setActivePage('staff-chat');
  };

  const firstLetter = (e) => ((e.display_name || e.username || '?').trim()[0] || '?').toUpperCase();

  const departments = useMemo(() => {
    const set = new Set(); entries.forEach(e => e.department && set.add(e.department));
    return Array.from(set).sort();
  }, [entries]);

  const filtered = useMemo(() => {
    return entries.filter(e => {
      if (deptFilter !== 'all' && e.department !== deptFilter) return false;
      if (statusFilter !== 'all' && (e.account_status || 'active') !== statusFilter) return false;
      if (contractFilter === 'signed' && !e.contract_accepted) return false;
      if (contractFilter === 'unsigned' && e.contract_accepted) return false;
      if (letterFilter && firstLetter(e) !== letterFilter) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        return (e.display_name || '').toLowerCase().includes(q)
          || (e.username || '').toLowerCase().includes(q)
          || (e.email || '').toLowerCase().includes(q)
          || (e.title || '').toLowerCase().includes(q)
          || (e.department || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [entries, deptFilter, statusFilter, contractFilter, searchTerm, letterFilter]);

  const sortedFiltered = useMemo(() => [...filtered].sort((a, b) => (a.display_name || a.username).localeCompare(b.display_name || b.username)), [filtered]);

  const grouped = useMemo(() => {
    const g = {};
    sortedFiltered.forEach(e => { const d = e.department || 'Unassigned'; (g[d] = g[d] || []).push(e); });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [sortedFiltered]);

  const lettersPresent = useMemo(() => new Set(entries.map(firstLetter)), [entries]);

  const deptChipOptions = [{ value: 'all', label: 'All', count: entries.length }].concat(
    departments.map(d => ({ value: d, label: d, count: entries.filter(e => e.department === d).length })));

  const stats = useMemo(() => ({
    total: entries.length,
    active: entries.filter(e => (e.account_status || 'active') === 'active').length,
    suspended: entries.filter(e => e.account_status === 'suspended').length,
    signed: entries.filter(e => e.contract_accepted).length,
    unsigned: entries.filter(e => !e.contract_accepted).length,
  }), [entries]);

  const openDetails = async (entry) => {
    try {
      const h = getAuthHeaders();
      const r = await axios.get(`${BACKEND_URL}/api/staff-directory/${entry.id}`, { headers: h });
      // Merge list-level fields (role, account_status) with detail-level (contract_acceptances, etc.)
      setSelectedEntry({ ...entry, ...r.data });
    } catch {
      setSelectedEntry(entry);
    }
    setShowDetails(true);
  };

  const startEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      ...emptyForm,
      ...Object.fromEntries(Object.entries(entry).filter(([k]) => k in emptyForm)),
    });
    setShowDetails(false);
    setShowForm(true);
  };

  const submitForm = async () => {
    if (!formData.username || !formData.display_name || !formData.title || !formData.email) return toast.error('Username, display name, title and email are required');
    try {
      const h = getAuthHeaders();
      const payload = {};
      const numeric = ['hourly_rate', 'salary'];
      Object.entries(formData).forEach(([k, v]) => {
        if (v === '' || v == null) return;
        if (numeric.includes(k)) { const n = parseFloat(v); if (!isNaN(n)) payload[k] = n; }
        else payload[k] = v;
      });
      if (!payload.department) payload.department = 'General';
      if (editingEntry) {
        await axios.put(`${BACKEND_URL}/api/staff-directory/${editingEntry.id}`, payload, { headers: h });
        toast.success('Entry updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/staff-directory/`, payload, { headers: h });
        toast.success('Entry created');
      }
      setShowForm(false); setEditingEntry(null); setFormData(emptyForm); fetchEntries();
    } catch (e) {
      const err = e.response?.data?.detail;
      toast.error(typeof err === 'string' ? err : Array.isArray(err) ? err.map(x => x.msg || x).join(', ') : 'Save failed');
    }
  };

  const removeEntry = async (entry) => {
    if (!window.confirm(`Delete directory entry for ${entry.display_name}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/staff-directory/${entry.id}`, { headers: getAuthHeaders() });
      toast.success('Entry deleted');
      setShowDetails(false); fetchEntries();
    } catch { toast.error('Delete failed'); }
  };

  const applyStatus = async () => {
    if (!statusAction.status || !selectedEntry) return;
    try {
      await axios.put(`${BACKEND_URL}/api/staff-directory/${selectedEntry.id}/account-status`, statusAction, { headers: getAuthHeaders() });
      toast.success(`Account ${statusAction.status}`);
      setShowStatusModal(false); setStatusAction({ status: '', reason: '' }); fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0]; if (!file || !selectedEntry) return;
    const docType = prompt('Document type? (e.g. ID, Contract, Passport, Proof of Address)');
    if (!docType) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData(); fd.append('document', file); fd.append('document_type', docType);
      const h = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/staff-directory/${selectedEntry.id}/documents`, fd, { headers: { ...h, 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      const r = await axios.get(`${BACKEND_URL}/api/staff-directory/${selectedEntry.id}`, { headers: h });
      setSelectedEntry(r.data);
    } catch { toast.error('Upload failed'); }
    setUploadingDoc(false);
  };

  const renderCard = (e, i) => (
    <ProfileCard key={e.id || e.username} index={i} entry={e} onOpen={openDetails} onMessage={openDM}
      online={onlineSet.has(e.username)} isSelf={e.username === currentUser?.username} isExecutive={isExecutive} />
  );

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-cyan-400 text-sm tracking-[0.2em] uppercase">Loading directory…</div></div>;
  }

  return (
    <div className="pb-12" data-testid="staff-directory-page">
      <div className="fixed top-0 left-1/4 w-[700px] h-[500px] bg-[#00E5FF]/[0.03] rounded-full blur-[180px] pointer-events-none -z-10" />
      <div className="fixed bottom-0 right-1/4 w-[600px] h-[400px] bg-[#8A2BE2]/[0.04] rounded-full blur-[160px] pointer-events-none -z-10" />

      {/* Hero */}
      <div className="relative overflow-hidden rounded-[32px] border border-white/[0.06] p-8 md:p-10 mb-6"
        style={{ background: 'linear-gradient(140deg, rgba(10,10,20,0.75) 0%, rgba(8,8,18,0.9) 100%)', boxShadow: '0 30px 80px -20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)' }}>
        <div className="pointer-events-none absolute -top-32 -right-24 w-96 h-96 rounded-full blur-[130px] bg-cyan-500/25" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 w-96 h-96 rounded-full blur-[130px] bg-violet-500/20" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] tracking-[0.28em] uppercase text-white/40 font-medium mb-1">People · HR Directory</p>
            <h1 className="text-white text-4xl md:text-5xl font-light tracking-tight">Staff Directory</h1>
            <p className="text-white/45 text-sm mt-2 max-w-xl">Every account, contract log, HR document and status control in one bento view.</p>
          </div>
          {isExecutive && (
            <button
              type="button"
              onClick={() => { setEditingEntry(null); setFormData(emptyForm); setShowForm(true); }}
              data-testid="add-staff-btn"
              className="group relative inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[11px] font-medium tracking-[0.15em] uppercase overflow-hidden transition-all hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #00E5FF, #8A2BE2)', color: '#0a0a14', boxShadow: '0 14px 30px -10px rgba(0,229,255,0.55)' }}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <Plus className="w-3.5 h-3.5 relative" />
              <span className="relative">Add staff</span>
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard icon={Users} label="Total" value={stats.total} accent="#00E5FF" sub={onlineSet.size ? `${onlineSet.size} online now` : ''} />
        <StatCard icon={CheckCircle} label="Active" value={stats.active} accent="#10B981" />
        <StatCard icon={AlertTriangle} label="Suspended" value={stats.suspended} accent="#F59E0B" />
        <StatCard icon={ShieldCheck} label="Signed" value={stats.signed} accent="#10B981" sub={stats.total ? `${Math.round(stats.signed / stats.total * 100)}% coverage` : ''} />
        <StatCard icon={ShieldAlert} label="Unsigned" value={stats.unsigned} accent="#F43F5E" sub={stats.unsigned > 0 ? 'Awaiting contracts' : 'All up to date'} />
      </div>

      {/* Filter bar */}
      <BentoCard accent="#00E5FF" className="mb-6">
        <div className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <input
              type="text"
              placeholder="Search name, email, title, department…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3.5 py-2.5 bg-black/40 border border-white/[0.07] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-cyan-400/40"
              data-testid="staff-search-input"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3.5 py-2.5 bg-black/40 border border-white/[0.07] rounded-xl text-white text-sm focus:outline-none focus:border-cyan-400/40" data-testid="staff-status-filter">
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="locked">Locked</option>
          </select>
          {isExecutive && (
            <select value={contractFilter} onChange={(e) => setContractFilter(e.target.value)} className="px-3.5 py-2.5 bg-black/40 border border-white/[0.07] rounded-xl text-white text-sm focus:outline-none focus:border-cyan-400/40" data-testid="staff-contract-filter">
              <option value="all">All contracts</option>
              <option value="signed">Signed</option>
              <option value="unsigned">Unsigned only</option>
            </select>
          )}
          <div className="flex rounded-xl border border-white/[0.07] bg-black/40 p-0.5">
            {[['grid', Grid3x3, 'Cards'], ['grouped', Layers, 'Group by department'], ['list', List, 'List']].map(([m, Ic, title]) => (
              <button key={m} onClick={() => setViewMode(m)} title={title} data-testid={`dir-view-mode-${m}`}
                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors flex items-center gap-1 ${viewMode === m ? 'bg-cyan-500/20 text-cyan-200' : 'text-white/40 hover:text-white/70'}`}>
                <Ic className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>
        </div>
        <div className="px-4 pb-4 space-y-3">
          <FilterChips label="Department" options={deptChipOptions} value={deptFilter} onChange={setDeptFilter} testIdPrefix="dir-dept-chip" />
          <DirectoryAlphaBar present={lettersPresent} active={letterFilter} onSelect={setLetterFilter} />
        </div>
      </BentoCard>

      {/* Grid */}
      {filtered.length === 0 ? (
        <BentoCard>
          <div className="p-16 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/[0.08] mb-3">
              <Users className="w-6 h-6 text-white/40" />
            </div>
            <p className="text-white/60">No staff match your filters</p>
          </div>
        </BentoCard>
      ) : viewMode === 'grid' ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="dir-card-grid">
          {sortedFiltered.map((e, i) => renderCard(e, i))}
        </div>
      ) : viewMode === 'grouped' ? (
        <div className="space-y-6" data-testid="dir-grouped">
          {grouped.map(([dept, list]) => (
            <section key={dept} data-testid={`dir-group-${dept.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center gap-3 mb-3">
                <Building className="w-3.5 h-3.5 text-cyan-300" />
                <p className="text-[10px] tracking-[0.22em] uppercase text-white/60 font-semibold">{dept}</p>
                <span className="text-[10px] text-white/35 tabular-nums">{list.length}</span>
                <div className="flex-1 h-px bg-gradient-to-r from-white/[0.08] to-transparent" />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">{list.map((e, i) => renderCard(e, i))}</div>
            </section>
          ))}
        </div>
      ) : (
        <BentoCard>
          <div className="divide-y divide-white/[0.05]">
            {sortedFiltered.map(e => {
              const role = ROLE_META[e.role] || ROLE_META.staff;
              const status = STATUS_META[e.account_status || 'active'] || STATUS_META.active;
              return (
                <button key={e.id || e.username} onClick={() => openDetails(e)} data-testid={`staff-row-${e.username}`} className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-white/[0.02] transition-colors">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${role.hex}25, rgba(138,43,226,0.20))`, border: `1px solid ${role.hex}40` }}>
                    {(e.display_name || e.username).split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{e.display_name || e.username}</p>
                    <p className="text-white/40 text-[11px] truncate">@{e.username} · {e.title || 'No title'} · {e.department || 'No dept'}</p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.12em] uppercase border flex-shrink-0" style={{ background: `${role.hex}18`, borderColor: `${role.hex}40`, color: role.hex }}>{role.label}</span>
                  <span className="inline-flex items-center gap-1 text-[9px] font-semibold tracking-[0.14em] uppercase w-24 flex-shrink-0" style={{ color: status.hex }}>
                    <status.icon className="w-2.5 h-2.5" /> {status.label}
                  </span>
                  {e.contract_accepted ? (
                    <span className="inline-flex items-center gap-1 text-emerald-300 text-[9px] font-semibold tracking-[0.14em] uppercase w-20 flex-shrink-0"><ShieldCheck className="w-2.5 h-2.5" /> Signed</span>
                  ) : isExecutive ? (
                    <span className="inline-flex items-center gap-1 text-rose-300 text-[9px] font-semibold tracking-[0.14em] uppercase w-20 flex-shrink-0"><ShieldAlert className="w-2.5 h-2.5" /> Unsigned</span>
                  ) : <span className="w-20 flex-shrink-0" />}
                  <ChevronRight className="w-4 h-4 text-white/25 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </BentoCard>
      )}

      {/* Details modal */}
      <DetailModal
        open={showDetails} onOpenChange={setShowDetails} entry={selectedEntry}
        isExecutive={isExecutive} isSuperadmin={isSuperadmin} isManagement={isManagement}
        onEdit={startEdit}
        onDelete={removeEntry}
        onStatus={(e) => { setSelectedEntry(e); setStatusAction({ status: e.account_status || 'active', reason: '' }); setShowDetails(false); setShowStatusModal(true); }}
        onUpload={uploadDoc}
        uploading={uploadingDoc}
      />

      {/* Add/edit form */}
      <JewelModal
        open={showForm} onOpenChange={setShowForm}
        title={editingEntry ? 'Edit Staff Entry' : 'New Staff Entry'}
        subtitle="Full HR record — visible per role"
        icon={editingEntry ? Edit3 : Plus} accent="cyan" size="2xl" testid="staff-form-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => { setShowForm(false); setEditingEntry(null); }}>Cancel</JewelButton>
          <JewelButton accent="cyan" icon={editingEntry ? Edit3 : Plus} onClick={submitForm} data-testid="staff-form-save-btn">{editingEntry ? 'Save changes' : 'Create entry'}</JewelButton>
        </>}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <JewelField label="Username" required><JewelInput value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} disabled={!!editingEntry} data-testid="form-username" /></JewelField>
            <JewelField label="Display name" required><JewelInput value={formData.display_name} onChange={(e) => setFormData({ ...formData, display_name: e.target.value })} data-testid="form-display-name" /></JewelField>
            <JewelField label="Title" required><JewelInput value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} /></JewelField>
            <JewelField label="Department"><JewelInput value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} /></JewelField>
            <JewelField label="Email" required><JewelInput type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></JewelField>
            <JewelField label="Phone"><JewelInput value={formData.phone_number} onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })} /></JewelField>
          </div>

          {isExecutive && (
            <>
              <div className="border-t border-white/[0.05] pt-4">
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-medium mb-2">Personal (executives only)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <JewelField label="Full legal name"><JewelInput value={formData.full_legal_name} onChange={(e) => setFormData({ ...formData, full_legal_name: e.target.value })} /></JewelField>
                  <JewelField label="Date of birth"><JewelInput type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} /></JewelField>
                  <JewelField label="Address"><JewelInput value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} /></JewelField>
                  <JewelField label="City"><JewelInput value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} /></JewelField>
                  <JewelField label="Postcode"><JewelInput value={formData.postcode} onChange={(e) => setFormData({ ...formData, postcode: e.target.value })} /></JewelField>
                  <JewelField label="Country"><JewelInput value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} /></JewelField>
                  <JewelField label="Emergency contact name"><JewelInput value={formData.emergency_contact_name} onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })} /></JewelField>
                  <JewelField label="Emergency contact phone"><JewelInput value={formData.emergency_contact_phone} onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })} /></JewelField>
                </div>
              </div>

              <div className="border-t border-white/[0.05] pt-4">
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-medium mb-2">Employment</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <JewelField label="Start date"><JewelInput type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} /></JewelField>
                  <JewelField label="Contract type">
                    <JewelSelect value={formData.contract_type} onChange={(e) => setFormData({ ...formData, contract_type: e.target.value })}>
                      <option value="full-time">Full-time</option>
                      <option value="part-time">Part-time</option>
                      <option value="contractor">Contractor</option>
                      <option value="internship">Internship</option>
                    </JewelSelect>
                  </JewelField>
                  <JewelField label="Direct manager">
                    <JewelSelect value={formData.direct_manager} onChange={(e) => setFormData({ ...formData, direct_manager: e.target.value })}>
                      <option value="">— None —</option>
                      {managersList.map(m => <option key={m.username} value={m.username}>{m.display_name || m.username} ({m.role})</option>)}
                    </JewelSelect>
                  </JewelField>
                  <JewelField label="Hourly rate £"><JewelInput type="number" step="0.01" value={formData.hourly_rate} onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })} /></JewelField>
                  <JewelField label="Salary £"><JewelInput type="number" step="1" value={formData.salary} onChange={(e) => setFormData({ ...formData, salary: e.target.value })} /></JewelField>
                </div>
              </div>

              <div className="border-t border-white/[0.05] pt-4">
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-medium mb-2">Bank &amp; identity</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <JewelField label="Bank name"><JewelInput value={formData.bank_account_name} onChange={(e) => setFormData({ ...formData, bank_account_name: e.target.value })} /></JewelField>
                  <JewelField label="Sort code"><JewelInput value={formData.bank_sort_code} onChange={(e) => setFormData({ ...formData, bank_sort_code: e.target.value })} placeholder="00-00-00" /></JewelField>
                  <JewelField label="Account number"><JewelInput value={formData.bank_account_number} onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })} /></JewelField>
                  <JewelField label="National Insurance"><JewelInput value={formData.national_insurance} onChange={(e) => setFormData({ ...formData, national_insurance: e.target.value })} placeholder="QQ 12 34 56 C" /></JewelField>
                </div>
              </div>

              <div className="border-t border-white/[0.05] pt-4">
                <JewelField label="HR notes">
                  <JewelTextarea rows={3} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
                </JewelField>
              </div>
            </>
          )}
        </div>
      </JewelModal>

      {/* Status modal */}
      <JewelModal
        open={showStatusModal} onOpenChange={setShowStatusModal}
        title="Change account status"
        subtitle={selectedEntry ? `@${selectedEntry.username}` : ''}
        icon={Lock} accent="rose" size="md" testid="status-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowStatusModal(false)}>Cancel</JewelButton>
          <JewelButton accent="rose" icon={CheckCircle} onClick={applyStatus} disabled={!statusAction.status} data-testid="status-apply-btn">Apply</JewelButton>
        </>}
      >
        <div className="space-y-3">
          <JewelField label="New status" required>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(STATUS_META).map(([id, meta]) => {
                const active = statusAction.status === id;
                return (
                  <button key={id} type="button" onClick={() => setStatusAction({ ...statusAction, status: id })} data-testid={`status-${id}`}
                    className="flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-all"
                    style={{ background: active ? `${meta.hex}12` : 'rgba(255,255,255,0.02)', borderColor: active ? `${meta.hex}45` : 'rgba(255,255,255,0.06)', color: active ? meta.hex : 'rgba(255,255,255,0.5)' }}>
                    <meta.icon className="w-4 h-4" />
                    <span className="text-[10px] tracking-[0.15em] uppercase font-medium">{meta.label}</span>
                  </button>
                );
              })}
            </div>
          </JewelField>
          {statusAction.status !== 'active' && (
            <JewelField label="Reason">
              <JewelTextarea rows={3} value={statusAction.reason} onChange={(e) => setStatusAction({ ...statusAction, reason: e.target.value })} placeholder="Why is this account being suspended or locked?" />
            </JewelField>
          )}
        </div>
      </JewelModal>
    </div>
  );
};

export default StaffDirectoryPage;
